import React from 'react';
import {
  Box,
  Typography,
  Button,
  alpha,
  useTheme
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import AccountBoxIcon from '@mui/icons-material/AccountBox';
import ReceiptIcon from '@mui/icons-material/Receipt';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import LocalMallIcon from '@mui/icons-material/LocalMall';
import ScienceIcon from '@mui/icons-material/Science';
import InventoryIcon from '@mui/icons-material/Inventory';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import FactoryIcon from '@mui/icons-material/Factory';
import OpacityIcon from '@mui/icons-material/Opacity';
import PeopleIcon from '@mui/icons-material/People';
import ScheduleIcon from '@mui/icons-material/Schedule';
import SettingsIcon from '@mui/icons-material/Settings';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import DescriptionIcon from '@mui/icons-material/Description';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';

export default function Navigation({ selectedCategory = 'dashboard', onCategoryChange }) {
  const theme = useTheme();

  return (
    <Box sx={{ 
      width: '220px',
      minWidth: '220px',
      borderRight: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
      p: 2,
      overflowY: 'auto',
      '&::-webkit-scrollbar': {
        width: '6px',
      },
      '&::-webkit-scrollbar-track': {
        background: alpha(theme.palette.grey[300], 0.3),
        borderRadius: '3px',
      },
      '&::-webkit-scrollbar-thumb': {
        background: alpha(theme.palette.grey[500], 0.5),
        borderRadius: '3px',
      },
    }}>
      <Typography variant="h6" sx={{ fontWeight: 500, mb: 2, fontSize: '0.9rem' }}>
        Navigation
      </Typography>
      
      {(() => {
        const categoryOrder = ['dashboard', 'calendar', 'locations', 'supply_chain', 'expenses', 'accounting', 'sales', 'products', 'formulations', 'inventory', 'manufacturing', 'batch_production', 'line_production', 'employees', 'schedule', 'settings'];
        const categoryMapping = {
          'dashboard': 'Dashboard',
          'calendar': 'Calendar',
          'locations': 'Locations',
          'expenses': 'Expenses',
          'accounting': 'Accounting',
          'sales': 'CRM',
          'products': 'Products',
          'formulations': 'Formulations',
          'supply_chain': 'Vendors',
          'inventory': 'Inventory',
          'manufacturing': 'Production Planning',
          'batch_production': 'Batch Production',
          'line_production': 'Line Production',
          'employees': 'Employees',
          'schedule': 'Schedule',
          'settings': 'Settings'
        };
        
        const categoryColors = {
          'dashboard': '#4CAF50',
          'calendar': '#FF5722',
          'locations': '#3F51B5',
          'expenses': '#C62828',
          'accounting': '#1B5E20',
          'sales': '#2196F3',
          'products': '#FF9800',
          'formulations': '#E91E63',
          'supply_chain': '#9C27B0',
          'inventory': '#009688',
          'manufacturing': '#FF4081',
          'batch_production': '#00BCD4',
          'line_production': '#FF6F00',
          'employees': '#FFC107',
          'schedule': '#673AB7',
          'settings': '#0277BD'
        };

        const categoryIcons = {
          'dashboard': DashboardIcon,
          'calendar': CalendarTodayIcon,
          'locations': LocationCityIcon,
          'expenses': ReceiptIcon,
          'accounting': AccountBalanceIcon,
          'sales': MonetizationOnIcon,
          'products': LocalMallIcon,
          'formulations': ScienceIcon,
          'supply_chain': AccountBoxIcon,
          'inventory': InventoryIcon,
          'manufacturing': CalendarMonthIcon,
          'batch_production': FactoryIcon,
          'line_production': OpacityIcon,
          'employees': PeopleIcon,
          'schedule': ScheduleIcon,
          'settings': SettingsIcon
        };

        return categoryOrder.map(categoryKey => {
          const categoryName = categoryMapping[categoryKey];
          const categoryColor = categoryColors[categoryKey] || '#1976d2';
          const categoryIcon = categoryIcons[categoryKey];
          
          return (
            <Box key={categoryKey} sx={{ mb: 1.5 }}>
              <Button
                fullWidth
                onClick={() => {
                  if (onCategoryChange) {
                    onCategoryChange(categoryKey);
                  }
                  const element = document.getElementById(`create-role-category-${categoryKey}`);
                  if (element) {
                    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }
                }}
                sx={{
                  justifyContent: 'flex-start',
                  textAlign: 'left',
                  py: 1,
                  px: 1.5,
                  borderRadius: 2,
                  color: selectedCategory === categoryKey ? categoryColor : 'text.primary',
                  fontWeight: 500,
                  fontSize: '0.8rem',
                  whiteSpace: 'nowrap',
                  background: selectedCategory === categoryKey 
                    ? `linear-gradient(135deg, ${alpha(categoryColor, 0.15)}, ${alpha(categoryColor, 0.08)})`
                    : `linear-gradient(135deg, ${alpha(categoryColor, 0.08)}, ${alpha(categoryColor, 0.03)})`,
                  border: selectedCategory === categoryKey
                    ? `2px solid ${alpha(categoryColor, 0.4)}`
                    : `1px solid ${alpha(categoryColor, 0.2)}`,
                  boxShadow: selectedCategory === categoryKey 
                    ? `0 2px 8px ${alpha(categoryColor, 0.25)}`
                    : 'none',
                  textTransform: 'none',
                  gap: 1.2,
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    background: `linear-gradient(135deg, ${alpha(categoryColor, 0.15)}, ${alpha(categoryColor, 0.06)})`,
                    borderColor: alpha(categoryColor, 0.4),
                    color: categoryColor,
                    transform: 'translateY(-1px)',
                    boxShadow: `0 2px 8px ${alpha(categoryColor, 0.2)}`,
                    '& > .MuiBox-root': {
                      transform: 'scale(1.1)',
                    }
                  }
                }}
              >
                <Box sx={{ 
                  color: categoryColor,
                  display: 'flex',
                  alignItems: 'center',
                  transition: 'transform 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'scale(1.1)'
                  }
                }}>
                  {categoryIcon && React.createElement(categoryIcon, { sx: { fontSize: 18 } })}
                </Box>
                {categoryName}
              </Button>
              
              {categoryKey === 'sales' && (
                <Box sx={{ ml: 1.5, mt: 0.5 }}>
                  <Button
                    key="purchase_orders"
                    fullWidth
                    size="small"
                    onClick={() => {
                      if (onCategoryChange) {
                        onCategoryChange('sales');
                      }
                      const element = document.getElementById('proforma-invoices-section');
                      if (element) {
                        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }
                    }}
                    sx={{
                      justifyContent: 'flex-start',
                      textAlign: 'left',
                      py: 0.5,
                      px: 1.2,
                      mb: 0.25,
                      borderRadius: 1.5,
                      color: '#E91E63',
                      fontWeight: 500,
                      fontSize: '0.7rem',
                      background: `linear-gradient(135deg, ${alpha('#E91E63', 0.08)}, ${alpha('#E91E63', 0.03)})`,
                      border: `1px solid ${alpha('#E91E63', 0.2)}`,
                      textTransform: 'none',
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        background: `linear-gradient(135deg, ${alpha('#E91E63', 0.15)}, ${alpha('#E91E63', 0.06)})`,
                        borderColor: alpha('#E91E63', 0.4),
                        color: '#E91E63',
                        transform: 'translateX(4px)',
                      }
                    }}
                  >
                    <ShoppingCartIcon sx={{ fontSize: 14, mr: 0.75 }} />
                    Orders
                  </Button>
                  <Box sx={{ ml: 1.5, mt: 0.25 }}>
                    <Button
                      key="purchase_order_workflow"
                      fullWidth
                      size="small"
                      onClick={() => {
                        if (onCategoryChange) {
                          onCategoryChange('sales');
                        }
                        const element = document.getElementById('purchase-order-workflow-section');
                        if (element) {
                          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }
                      }}
                      sx={{
                        justifyContent: 'flex-start',
                        textAlign: 'left',
                        py: 0.4,
                        px: 1,
                        mb: 0.15,
                        borderRadius: 1.5,
                        color: '#26A69A',
                        fontWeight: 500,
                        fontSize: '0.65rem',
                        background: `linear-gradient(135deg, ${alpha('#26A69A', 0.08)}, ${alpha('#26A69A', 0.03)})`,
                        border: `1px solid ${alpha('#26A69A', 0.2)}`,
                        textTransform: 'none',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          background: `linear-gradient(135deg, ${alpha('#26A69A', 0.15)}, ${alpha('#26A69A', 0.06)})`,
                          borderColor: alpha('#26A69A', 0.4),
                          color: '#26A69A',
                          transform: 'translateX(4px)',
                        }
                      }}
                    >
                      <DescriptionIcon sx={{ fontSize: 12, mr: 0.75 }} />
                      PI Workflow
                    </Button>
                    <Button
                      key="delivery_confirmation_workflow"
                      fullWidth
                      size="small"
                      onClick={() => {
                        if (onCategoryChange) {
                          onCategoryChange('sales');
                        }
                        const element = document.getElementById('purchase-order-confirmation-section');
                        if (element) {
                          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }
                      }}
                      sx={{
                        justifyContent: 'flex-start',
                        textAlign: 'left',
                        py: 0.4,
                        px: 1,
                        mb: 0.15,
                        borderRadius: 1.5,
                        color: '#9C27B0',
                        fontWeight: 500,
                        fontSize: '0.65rem',
                        background: `linear-gradient(135deg, ${alpha('#9C27B0', 0.08)}, ${alpha('#9C27B0', 0.03)})`,
                        border: `1px solid ${alpha('#9C27B0', 0.2)}`,
                        textTransform: 'none',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          background: `linear-gradient(135deg, ${alpha('#9C27B0', 0.15)}, ${alpha('#9C27B0', 0.06)})`,
                          borderColor: alpha('#9C27B0', 0.4),
                          color: '#9C27B0',
                          transform: 'translateX(4px)',
                        }
                      }}
                    >
                      <LocalShippingIcon sx={{ fontSize: 12, mr: 0.75 }} />
                      Delivery Confirmation
                    </Button>
                  </Box>
                </Box>
              )}
              
              {categoryKey === 'inventory' && (
                <Box sx={{ ml: 1.5, mt: 0.5 }}>
                  {['inventory', 'imports_exports'].map(invType => (
                    <Button
                      key={invType}
                      fullWidth
                      size="small"
                      onClick={() => {
                        if (onCategoryChange) {
                          onCategoryChange('inventory');
                        }
                        const sectionMapping = {
                          'inventory': 'inventory-section',
                          'imports_exports': 'imports-exports-section',
                        };

                        const sectionId = sectionMapping[invType];
                        if (sectionId) {
                          const targetElement = document.getElementById(sectionId);
                          if (targetElement) {
                            targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
                          }
                        }
                      }}
                      sx={{
                        justifyContent: 'flex-start',
                        textAlign: 'left',
                        py: 0.4,
                        px: 1,
                        mb: 0.2,
                        borderRadius: 1.5,
                        color: 'text.secondary',
                        fontWeight: 500,
                        fontSize: '0.7rem',
                        background: `linear-gradient(135deg, ${alpha('#009688', 0.05)}, ${alpha('#009688', 0.02)})`,
                        border: `1px solid ${alpha('#009688', 0.15)}`,
                        textTransform: 'none',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          background: `linear-gradient(135deg, ${alpha('#009688', 0.1)}, ${alpha('#009688', 0.04)})`,
                          borderColor: alpha('#009688', 0.3),
                          color: '#009688',
                          transform: 'translateX(4px)',
                        }
                      }}
                    >
                      {invType === 'inventory' ? 'Inventory' : 'Imports & Exports'}
                    </Button>
                  ))}
                </Box>
              )}
              
              {categoryKey === 'formulations' && (
                <Box sx={{ ml: 1.5, mt: 0.5 }}>
                  <Button
                    key="qc_tests"
                    fullWidth
                    size="small"
                    onClick={() => {
                      if (onCategoryChange) {
                        onCategoryChange('formulations');
                      }
                      const element = document.getElementById('material-quality-check-section');
                      if (element) {
                        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }
                    }}
                    sx={{
                      justifyContent: 'flex-start',
                      textAlign: 'left',
                      py: 0.5,
                      px: 1.2,
                      mb: 0.25,
                      borderRadius: 1.5,
                      color: '#7B1FA2',
                      fontWeight: 500,
                      fontSize: '0.7rem',
                      background: `linear-gradient(135deg, ${alpha('#7B1FA2', 0.08)}, ${alpha('#7B1FA2', 0.03)})`,
                      border: `1px solid ${alpha('#7B1FA2', 0.2)}`,
                      textTransform: 'none',
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        background: `linear-gradient(135deg, ${alpha('#7B1FA2', 0.15)}, ${alpha('#7B1FA2', 0.06)})`,
                        borderColor: alpha('#7B1FA2', 0.4),
                        color: '#7B1FA2',
                        transform: 'translateX(4px)',
                      }
                    }}
                  >
                    Material Quality Check
                  </Button>
                </Box>
              )}

              {categoryKey === 'batch_production' && (
                <Box sx={{ ml: 1.5, mt: 0.5 }}>
                  <Button
                    key="batch_mixing"
                    fullWidth
                    size="small"
                    onClick={() => {
                      if (onCategoryChange) {
                        onCategoryChange('batch_production');
                      }
                      const element = document.getElementById(`create-role-subcategory-batch_mixing`);
                      if (element) {
                        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }
                    }}
                    sx={{
                      justifyContent: 'flex-start',
                      textAlign: 'left',
                      py: 0.5,
                      px: 1.2,
                      mb: 0.25,
                      borderRadius: 1.5,
                      color: '#FF9800',
                      fontWeight: 500,
                      fontSize: '0.7rem',
                      background: `linear-gradient(135deg, ${alpha('#FF9800', 0.08)}, ${alpha('#FF9800', 0.03)})`,
                      border: `1px solid ${alpha('#FF9800', 0.2)}`,
                      textTransform: 'none',
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        background: `linear-gradient(135deg, ${alpha('#FF9800', 0.15)}, ${alpha('#FF9800', 0.06)})`,
                        borderColor: alpha('#FF9800', 0.4),
                        color: '#FF9800',
                        transform: 'translateX(4px)',
                      }
                    }}
                  >
                    Batch Mixing
                  </Button>
                  <Box sx={{ ml: 1.5, mt: 0.25 }}>
                    <Button
                      key="batch_logs"
                      fullWidth
                      size="small"
                      onClick={() => {
                        if (onCategoryChange) {
                          onCategoryChange('batch_production');
                        }
                        const element = document.getElementById(`create-role-subcategory-batch_logs`);
                        if (element) {
                          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }
                      }}
                      sx={{
                        justifyContent: 'flex-start',
                        textAlign: 'left',
                        py: 0.4,
                        px: 1,
                        mb: 0.15,
                        borderRadius: 1.5,
                        color: '#009688',
                        fontWeight: 500,
                        fontSize: '0.7rem',
                        background: `linear-gradient(135deg, ${alpha('#009688', 0.08)}, ${alpha('#009688', 0.03)})`,
                        border: `1px solid ${alpha('#009688', 0.2)}`,
                        textTransform: 'none',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          background: `linear-gradient(135deg, ${alpha('#009688', 0.15)}, ${alpha('#009688', 0.06)})`,
                          borderColor: alpha('#009688', 0.4),
                          color: '#009688',
                          transform: 'translateX(4px)',
                        }
                      }}
                    >
                      Batch Log
                    </Button>
                  </Box>
                </Box>
              )}
              
              {categoryKey === 'line_production' && (
                <Box sx={{ ml: 1.5, mt: 0.5 }}>
                  <Button
                    key="line_assembly"
                    fullWidth
                    size="small"
                    onClick={() => {
                      if (onCategoryChange) {
                        onCategoryChange('line_production');
                      }
                      const element = document.getElementById(`create-role-subcategory-line_assembly`);
                      if (element) {
                        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }
                    }}
                    sx={{
                      justifyContent: 'flex-start',
                      textAlign: 'left',
                      py: 0.5,
                      px: 1.2,
                      mb: 0.25,
                      borderRadius: 1.5,
                      color: '#D32F2F',
                      fontWeight: 500,
                      fontSize: '0.7rem',
                      background: `linear-gradient(135deg, ${alpha('#D32F2F', 0.08)}, ${alpha('#D32F2F', 0.03)})`,
                      border: `1px solid ${alpha('#D32F2F', 0.2)}`,
                      textTransform: 'none',
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        background: `linear-gradient(135deg, ${alpha('#D32F2F', 0.15)}, ${alpha('#D32F2F', 0.06)})`,
                        borderColor: alpha('#D32F2F', 0.4),
                        color: '#D32F2F',
                        transform: 'translateX(4px)',
                      }
                    }}
                  >
                    Line Assembly
                  </Button>
                  <Box sx={{ ml: 1.5, mt: 0.25 }}>
                    <Button
                      key="line_production_log"
                      fullWidth
                      size="small"
                      onClick={() => {
                        if (onCategoryChange) {
                          onCategoryChange('line_production');
                        }
                        const element = document.getElementById(`create-role-subcategory-line_production_log`);
                        if (element) {
                          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }
                      }}
                      sx={{
                        justifyContent: 'flex-start',
                        textAlign: 'left',
                        py: 0.4,
                        px: 1,
                        mb: 0.15,
                        borderRadius: 1.5,
                        color: '#1565C0',
                        fontWeight: 500,
                        fontSize: '0.7rem',
                        background: `linear-gradient(135deg, ${alpha('#1565C0', 0.08)}, ${alpha('#1565C0', 0.03)})`,
                        border: `1px solid ${alpha('#1565C0', 0.2)}`,
                        textTransform: 'none',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          background: `linear-gradient(135deg, ${alpha('#1565C0', 0.15)}, ${alpha('#1565C0', 0.06)})`,
                          borderColor: alpha('#1565C0', 0.4),
                          color: '#1565C0',
                          transform: 'translateX(4px)',
                        }
                      }}
                    >
                      Line Production Log
                    </Button>
                  </Box>
                </Box>
              )}
              
              {categoryKey === 'settings' && (
                <Box sx={{ ml: 1.5, mt: 0.5 }}>
                  <Button
                    key="user_management"
                    fullWidth
                    size="small"
                    onClick={() => {
                      if (onCategoryChange) {
                        onCategoryChange('settings');
                      }
                      const element = document.getElementById(`create-role-subcategory-user_management`);
                      if (element) {
                        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }
                    }}
                    sx={{
                      justifyContent: 'flex-start',
                      textAlign: 'left',
                      py: 0.5,
                      px: 1.2,
                      mb: 0.25,
                      borderRadius: 1.5,
                      color: 'text.secondary',
                      fontWeight: 500,
                      fontSize: '0.7rem',
                      background: `linear-gradient(135deg, ${alpha('#0277BD', 0.05)}, ${alpha('#0277BD', 0.02)})`,
                      border: `1px solid ${alpha('#0277BD', 0.15)}`,
                      textTransform: 'none',
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        background: `linear-gradient(135deg, ${alpha('#0277BD', 0.1)}, ${alpha('#0277BD', 0.04)})`,
                        borderColor: alpha('#0277BD', 0.3),
                        color: '#0277BD',
                        transform: 'translateX(4px)',
                      }
                    }}
                  >
                    User Management
                  </Button>
                  <Button
                    key="company_settings"
                    fullWidth
                    size="small"
                    onClick={() => {
                      if (onCategoryChange) {
                        onCategoryChange('settings');
                      }
                      const element = document.getElementById(`create-role-subcategory-company_settings`);
                      if (element) {
                        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }
                    }}
                    sx={{
                      justifyContent: 'flex-start',
                      textAlign: 'left',
                      py: 0.5,
                      px: 1.2,
                      mb: 0.25,
                      borderRadius: 1.5,
                      color: 'text.secondary',
                      fontWeight: 500,
                      fontSize: '0.7rem',
                      background: `linear-gradient(135deg, ${alpha('#0277BD', 0.05)}, ${alpha('#0277BD', 0.02)})`,
                      border: `1px solid ${alpha('#0277BD', 0.15)}`,
                      textTransform: 'none',
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        background: `linear-gradient(135deg, ${alpha('#0277BD', 0.1)}, ${alpha('#0277BD', 0.04)})`,
                        borderColor: alpha('#0277BD', 0.3),
                        color: '#0277BD',
                        transform: 'translateX(4px)',
                      }
                    }}
                  >
                    Company Settings
                  </Button>
                </Box>
              )}
            </Box>
          );
        });
      })()}
    </Box>
  );
}